import { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { BA_CASES } from "../../data/site";
import { Reveal, SectionMark } from "./Reveal";

const BEFORE_FILTER =
    "grayscale(0.9) brightness(0.58) contrast(0.9) saturate(0.5)";

export const Transformation = () => {
    const [active, setActive] = useState(BA_CASES[0]);
    const [pos, setPos] = useState(50);
    const ref = useRef(null);
    const dragging = useRef(false);

    const update = useCallback((clientX) => {
        const rect = ref.current?.getBoundingClientRect();
        if (!rect) return;
        const p = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.min(98, Math.max(2, p)));
    }, []);

    const onPointerDown = (e) => {
        dragging.current = true;
        ref.current?.setPointerCapture?.(e.pointerId);
        update(e.clientX);
    };
    const onPointerMove = (e) => dragging.current && update(e.clientX);
    const stop = () => (dragging.current = false);

    const onKeyDown = (e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(2, p - 4));
        if (e.key === "ArrowRight") setPos((p) => Math.min(98, p + 4));
    };

    return (
        <section
            data-testid="transformation-section"
            className="relative py-24 md:py-36"
        >
            <div className="mb-10 flex flex-col gap-8 px-5 md:mb-14 md:flex-row md:items-end md:justify-between md:px-10">
                <div>
                    <Reveal>
                        <SectionMark>( 03 — Transformacija )</SectionMark>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-[11vw] font-light leading-none tracking-tight text-bone md:text-[6.5vw]">
                            PRIJE <span className="italic text-bronze">/</span>{" "}
                            POSLIJE
                        </h2>
                    </Reveal>
                </div>
                <Reveal delay={0.2} className="flex gap-6">
                    {BA_CASES.map((c) => (
                        <button
                            key={c.id}
                            data-testid={`ba-tab-${c.id}`}
                            onClick={() => {
                                setActive(c);
                                setPos(50);
                            }}
                            className={`border-b pb-1.5 text-[11px] font-medium uppercase tracking-[0.24em] transition-colors duration-300 ${
                                active.id === c.id
                                    ? "border-bronze text-bone"
                                    : "border-transparent text-silver hover:text-bone"
                            }`}
                        >
                            {c.label}
                        </button>
                    ))}
                </Reveal>
            </div>

            <Reveal delay={0.1}>
                <div
                    ref={ref}
                    data-testid="before-after-slider"
                    role="slider"
                    aria-label="Usporedba prije i poslije"
                    aria-valuenow={Math.round(pos)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    tabIndex={0}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={stop}
                    onPointerCancel={stop}
                    onKeyDown={onKeyDown}
                    className="relative h-[58svh] w-full cursor-ew-resize touch-none select-none overflow-hidden outline-none focus-visible:ring-1 focus-visible:ring-bronze md:h-[80svh]"
                >
                    <img
                        src={active.before || active.src}
                        alt={`${active.alt} — prije`}
                        draggable={false}
                        style={active.before ? undefined : { filter: BEFORE_FILTER }}
                        className="absolute inset-0 h-full w-full object-cover"
                    />
                    <div
                        className="absolute inset-0"
                        style={{ clipPath: `inset(0 0 0 ${pos}%)` }}
                    >
                        <img
                            src={active.after || active.src}
                            alt={`${active.alt} — poslije`}
                            draggable={false}
                            className="absolute inset-0 h-full w-full object-cover"
                        />
                    </div>

                    <span
                        data-testid="ba-label-before"
                        className="absolute left-5 top-5 border border-bone/20 px-3 py-1.5 text-[10px] font-medium tracking-[0.34em] text-bone/80 backdrop-blur-sm md:left-10 md:top-8"
                    >
                        PRIJE
                    </span>
                    <span
                        data-testid="ba-label-after"
                        className="absolute right-5 top-5 border border-bronze/60 px-3 py-1.5 text-[10px] font-medium tracking-[0.34em] text-bronze backdrop-blur-sm md:right-10 md:top-8"
                    >
                        POSLIJE
                    </span>

                    <div
                        data-testid="ba-handle"
                        className="absolute inset-y-0 z-10"
                        style={{ left: `${pos}%` }}
                    >
                        <div className="absolute inset-y-0 -left-px w-px bg-bone/90" />
                        <div className="absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center border border-bone/60 bg-[#0a0a0b]/70 text-bone backdrop-blur-sm">
                            <MoveHorizontal size={18} strokeWidth={1.5} />
                        </div>
                    </div>
                </div>
            </Reveal>

            <Reveal delay={0.15} className="px-5 text-center">
                <p className="mt-12 font-display text-2xl font-light italic tracking-tight text-bone md:mt-16 md:text-4xl">
                    Razlika koja se vidi.
                </p>
            </Reveal>
        </section>
    );
};
